import React, { Fragment, useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import classNames from 'classnames';
import pluralize from 'pluralize';

import FormGroup from '../../../../../../../../components/global/FormGroup';
import Button from '../../../../../../../../components/global/Button';
import PhoneInput from '../../../../../../../../components/global/PhoneInput';
import { CSV_DATE_FORMAT } from '../../../../hooks/useRosterIdentitiesCreate/helpers';
import AddressFormModal from '../AddressFormModal';
import Alert from '../../../../../../../../components/global/Alert';
import WarningIcon from '../../../../../../../../components/icons/WarningIcon';

const propTypes = {
  createdRosterIdentities: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string,
    }).isRequired,
  ),
  invalidRosterIdentities: PropTypes.arrayOf(
    PropTypes.shape({
      rosterIdentity: PropTypes.shape({
        id: PropTypes.string,
      }),
      errorFields: PropTypes.arrayOf(PropTypes.string),
      error: PropTypes.string,
    }),
  ),
  loading: PropTypes.bool,
  validateRosterIdentities: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onCancel: PropTypes.func.isRequired,
};

const InvalidRowEditor = ({
  createdRosterIdentities = [],
  invalidRosterIdentities = [],
  validateRosterIdentities,
  loading,
  onSubmit,
  onCancel,
}) => {
  const [rosterIdentities, setRosterIdentities] = useState(
    invalidRosterIdentities.map(({ rosterIdentity }) => rosterIdentity),
  );

  useEffect(() => {
    setRosterIdentities(
      invalidRosterIdentities.map(({ rosterIdentity }) => rosterIdentity),
    );
  }, [invalidRosterIdentities]);

  /**
   * @typedef EditRosterIdentityAddress
   * @type {object}
   * @property {function} handleChange The handle change method for this roster identity.
   * @property {object} rosterIdentity - The full roster identity object.
   * @property {object} [mainLinkedIdentity] - Main linked identity to get address from.
   */

  /**
   * @type {[EditRosterIdentityAddress, function]}
   */
  const [editRosterIdentityAddress, setEditRosterIdentityAddress] =
    useState(null);

  const onRosterIdentityChange = rosterIdentityId => {
    return updatedRosterIdentity => {
      setRosterIdentities(
        rosterIdentities.map(rosterIdentity =>
          rosterIdentity.id === rosterIdentityId
            ? {
                ...rosterIdentity,
                ...updatedRosterIdentity,
              }
            : rosterIdentity,
        ),
      );
    };
  };

  const { invalidRosterIdentities: invalidRosterIdentitiesCurrent } =
    validateRosterIdentities({ rosterIdentities });

  const totalErrorFields = invalidRosterIdentities.reduce(
    (sum, rosterIdentity) => sum + rosterIdentity.errorFields.length,
    0,
  );

  const hasPhoneNumberError = invalidRosterIdentities.some(({ errorFields }) =>
    errorFields.some(field => field.startsWith('phone_number')),
  );

  const isFormValid = invalidRosterIdentitiesCurrent.length === 0;

  return (
    <Fragment>
      {rosterIdentities.length > 0 && (
        <Fragment>
          <Alert type="info">
            <h4 className="alert-heading">
              <WarningIcon size="16px" color="#1a2e34" className="mr-1" /> Error
              importing CSV
            </h4>
            <p className="mb-1">
              A total of {totalErrorFields}{' '}
              {pluralize('error', totalErrorFields)} were found with{' '}
              {invalidRosterIdentities.length}{' '}
              {pluralize('contact', invalidRosterIdentities.length)}, noted in
              red below. Please correct these errors and click Continue.
            </p>

            {hasPhoneNumberError && (
              <p className="mb-1">
                Make sure your phone numbers are formatted like this:{' '}
                <span className="text-primary text-bold">+1 111 1111 1111</span>{' '}
                then try again.
              </p>
            )}

            <p className="m-0">
              A total of {createdRosterIdentities.length}{' '}
              <strong>
                {pluralize('contact', createdRosterIdentities.length)}
              </strong>{' '}
              have been created.
            </p>
          </Alert>

          <div className="table-responsive">
            <table className="table table-striped mb-3">
              <thead>
                <tr>
                  <th>First Name</th>
                  <th>Last Name</th>
                  <th>Date of Birth</th>
                  <th>Mobile Phone</th>
                  <th>Address</th>
                  <th>&nbsp;</th>
                </tr>
              </thead>
              <tbody>
                {rosterIdentities.map(rosterIdentity => {
                  const invalidRosterIdentity =
                    invalidRosterIdentitiesCurrent.find(
                      invalidRosterIdentityRecord =>
                        invalidRosterIdentityRecord.rosterIdentity.id ===
                        rosterIdentity.id,
                    );

                  const handleChange = onRosterIdentityChange(
                    rosterIdentity.id,
                  );

                  const mainLinkedIdentity =
                    // Do not update with getRosterIdentityMainLinkedIdentity. This doesn't have records.
                    rosterIdentity.linked_identities[0];

                  const { errorFields = [] } = invalidRosterIdentity || {};

                  return (
                    <tr key={rosterIdentity.id}>
                      <td>
                        <FormGroup
                          id={`${rosterIdentity.id}-first-name`}
                          name="first_name"
                          type="text"
                          placeholder="First name.."
                          value={rosterIdentity.first_name}
                          onChange={event => {
                            const { name, value } = event.target;
                            handleChange({ [name]: value });
                          }}
                          label="First Name"
                          labelSrOnly
                          size="sm"
                          className={classNames({
                            'm-0': true,
                            'form-control-error':
                              errorFields.includes('first_name'),
                          })}
                        />
                      </td>
                      <td>
                        <FormGroup
                          id={`${rosterIdentity.id}-last-name`}
                          name="last_name"
                          type="text"
                          label="Last Name"
                          placeholder="Last name.."
                          value={rosterIdentity.last_name}
                          onChange={event => {
                            const { name, value } = event.target;
                            handleChange({ [name]: value });
                          }}
                          labelSrOnly
                          size="sm"
                          className={classNames({
                            'm-0': true,
                            'form-control-error':
                              errorFields.includes('last_name'),
                          })}
                        />
                      </td>
                      <td>
                        <DatePicker
                          id={`${rosterIdentity.id}-date-of-birth`}
                          name="date_of_birth"
                          labelSrOnly
                          size="sm"
                          className={classNames({
                            'form-control form-control-sm m-0': true,
                            'form-control-error':
                              errorFields.includes('date_of_birth'),
                          })}
                          isClearable
                          selected={
                            rosterIdentity.date_of_birth &&
                            moment(
                              rosterIdentity.date_of_birth,
                              CSV_DATE_FORMAT,
                              true,
                            ).isValid()
                              ? rosterIdentity.date_of_birth
                              : null
                          }
                          onChange={date => {
                            handleChange({
                              date_of_birth: date,
                            });
                          }}
                        />
                      </td>
                      <td>
                        <PhoneInput
                          id={`${rosterIdentity.id}-phone-number`}
                          label="Mobile Phone"
                          value={mainLinkedIdentity?.phone_number || '+1'}
                          onChange={phone => {
                            handleChange({
                              linked_identities: [
                                {
                                  ...mainLinkedIdentity,
                                  phone_number: phone,
                                },
                              ],
                            });
                          }}
                          autoComplete="off"
                          labelSrOnly
                          size="sm"
                          className={classNames({
                            'm-0': true,
                            'form-control-error': errorFields.some(field =>
                              field.startsWith('phone_number'),
                            ),
                          })}
                        />
                      </td>
                      <td>
                        <Button
                          btnStyle="reset"
                          btnSize="sm"
                          onClick={() => {
                            setEditRosterIdentityAddress({
                              handleChange,
                              rosterIdentity,
                              mainLinkedIdentity,
                            });
                          }}
                          className={classNames('text-center', {
                            'text-danger': errorFields.includes('address'),
                          })}
                        >
                          {mainLinkedIdentity?.addresses?.[0].address_line_1 ??
                            'Not provided'}{' '}
                          <span className="text-xs text-primary ml-1">
                            Edit
                          </span>
                        </Button>
                      </td>
                      <td>
                        <Button
                          btnStyle="link"
                          onClick={() => {
                            setRosterIdentities(
                              rosterIdentities.filter(
                                ({ id }) => id !== rosterIdentity.id,
                              ),
                            );
                          }}
                          className="text-danger"
                        >
                          &times;
                        </Button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="d-flex mt-2">
            <Button
              btnStyle="secondary"
              onClick={onCancel}
              className="ml-auto mr-1"
              disabled={loading}
              btnSize="sm"
            >
              Cancel Import
            </Button>
            <Button
              onClick={() => onSubmit(rosterIdentities)}
              disabled={loading || !isFormValid}
            >
              {loading ? 'Loading...' : 'Continue'}
            </Button>
          </div>
        </Fragment>
      )}

      {rosterIdentities.length === 0 && (
        <Fragment>
          <Alert type="info">
            <h4 className="alert-heading">No records found</h4>
            <p className="mb-1">
              CSV import finished and all errors have been resolved.
              <br />
              Click close and refresh the page to see all the imported members.
            </p>
          </Alert>

          <div className="d-flex mt-2">
            <Button
              btnStyle="secondary"
              onClick={onCancel}
              className="ml-auto mr-1"
              btnSize="sm"
            >
              Close
            </Button>
          </div>
        </Fragment>
      )}

      {editRosterIdentityAddress && (
        <AddressFormModal
          forceOpen
          address={editRosterIdentityAddress.mainLinkedIdentity?.addresses?.[0]}
          onCancel={() => setEditRosterIdentityAddress(null)}
          onSuccess={address => {
            editRosterIdentityAddress.handleChange({
              linked_identities: [
                {
                  ...(editRosterIdentityAddress.mainLinkedIdentity || {}),
                  addresses: [address],
                },
              ],
            });

            setEditRosterIdentityAddress(null);
          }}
        />
      )}
    </Fragment>
  );
};

InvalidRowEditor.propTypes = propTypes;

export default InvalidRowEditor;
